import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Grid, List, ArrowLeft, Trophy, SlidersHorizontal, Navigation } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SearchBar } from '@/components/search/SearchBar';
import { SaveSearchDialog } from '@/components/search/SaveSearchDialog';
import { PropertyGrid } from '@/components/property/PropertyGrid';
import { PropertyCard } from '@/components/property/PropertyCard';
import { NeighborhoodIntro } from '@/components/search/NeighborhoodIntro';
import { RankedPropertyCard } from '@/components/rankings/RankedPropertyCard';
import { ViewingPlanCTA } from '@/components/ViewingPlanCTA';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useProperties, usePropertiesCount, useInfiniteProperties, type PropertySortOption } from '@/hooks/useProperties';
import { Loader2 } from 'lucide-react';
import { useRankedListingsByNeighborhood } from '@/hooks/useRankedListings';
import { useCity } from '@/contexts/CityContext';
import { PropertyFilters, Property } from '@/types/property';
import { getEdmontonRegionNeighborhoods, getEdmontonRegionTitle } from '@/lib/regionFilters';

type ViewMode = 'grid' | 'list';
type SortOption = 'newest' | 'price-asc' | 'price-desc' | 'beds-desc' | 'nearest';

// Haversine formula for distance calculation
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { city: defaultCity } = useCity();
  const region = searchParams.get('region');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Nearby mode params
  const nearbyLat = searchParams.get('lat');
  const nearbyLng = searchParams.get('lng');
  const isNearby = searchParams.get('nearby') === 'true' && !!nearbyLat && !!nearbyLng;

  const [sortBy, setSortBy] = useState<SortOption>(isNearby ? 'nearest' : 'newest');

  // Derive filters reactively from URL params so browser back/forward works
  const filters = useMemo<PropertyFilters>(() => ({
    searchQuery: region ? undefined : searchParams.get('q') || undefined,
    city: searchParams.get('city') || undefined,
    neighborhoods: getEdmontonRegionNeighborhoods(region),
    propertyType: searchParams.get('type') || undefined,
    neighborhood: searchParams.get('neighborhood') || undefined,
    minBedrooms: searchParams.get('beds') ? parseInt(searchParams.get('beds')!) : undefined,
    minBathrooms: searchParams.get('baths') ? parseInt(searchParams.get('baths')!) : undefined,
    minPrice: searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')!) : undefined,
    maxPrice: searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')!) : undefined,
    minSqft: searchParams.get('minSqft') ? parseInt(searchParams.get('minSqft')!) : undefined,
    maxSqft: searchParams.get('maxSqft') ? parseInt(searchParams.get('maxSqft')!) : undefined,
    minYearBuilt: searchParams.get('minYear') ? parseInt(searchParams.get('minYear')!) : undefined,
    maxYearBuilt: searchParams.get('maxYear') ? parseInt(searchParams.get('maxYear')!) : undefined,
    parking: searchParams.get('parking') || undefined,
    distressed: searchParams.get('distressed') === 'true' || undefined,
    distressedType: (['bank', 'court', 'probate'] as const).find(
      (t) => t === searchParams.get('dtype'),
    ),
    architecturalStyles: searchParams.get('styles')?.split(',').filter(Boolean) || undefined,
    propertyConditions: searchParams.get('conditions')?.split(',').filter(Boolean) || undefined,
  }), [searchParams, region]);

  // When user updates filters via SearchBar, sync to URL
  const handleSearch = useCallback((newFilters: PropertyFilters) => {
    const params = new URLSearchParams();
    if (newFilters.searchQuery) params.set('q', newFilters.searchQuery);
    if (newFilters.city) params.set('city', newFilters.city);
    if (newFilters.propertyType) params.set('type', newFilters.propertyType);
    if (newFilters.neighborhood) params.set('neighborhood', newFilters.neighborhood);
    if (newFilters.minBedrooms) params.set('beds', String(newFilters.minBedrooms));
    if (newFilters.minBathrooms) params.set('baths', String(newFilters.minBathrooms));
    if (newFilters.minPrice) params.set('minPrice', String(newFilters.minPrice));
    if (newFilters.maxPrice) params.set('maxPrice', String(newFilters.maxPrice));
    if (newFilters.minSqft) params.set('minSqft', String(newFilters.minSqft));
    if (newFilters.maxSqft) params.set('maxSqft', String(newFilters.maxSqft));
    if (newFilters.minYearBuilt) params.set('minYear', String(newFilters.minYearBuilt));
    if (newFilters.maxYearBuilt) params.set('maxYear', String(newFilters.maxYearBuilt));
    if (newFilters.parking) params.set('parking', newFilters.parking);
    if (newFilters.distressed) params.set('distressed', 'true');
    if (newFilters.distressedType) params.set('dtype', newFilters.distressedType);
    if (newFilters.architecturalStyles?.length) params.set('styles', newFilters.architecturalStyles.join(','));
    if (newFilters.propertyConditions?.length) params.set('conditions', newFilters.propertyConditions.join(','));
    // Preserve nearby params when applying filters
    if (nearbyLat) params.set('lat', nearbyLat);
    if (nearbyLng) params.set('lng', nearbyLng);
    if (isNearby) params.set('nearby', 'true');
    setSearchParams(params, { replace: false });
  }, [setSearchParams, nearbyLat, nearbyLng, isNearby]);

  const isDistressedMode = filters.distressed;

  const effectiveCity = filters.city || defaultCity;
  const combinedFilters: PropertyFilters = {
    ...filters,
    city: filters.searchQuery ? filters.city : effectiveCity,
  };
  // Nearby mode still pulls the full result set so we can sort by distance
  // client-side; everything else uses infinite scroll for fast initial paint.
  const useInfinite = !isNearby;

  const {
    data: nearbyProperties,
    isLoading: isNearbyLoading,
    isError: isNearbyError,
    isFetching: isNearbyFetching,
    refetch: refetchNearby,
  } = useProperties(combinedFilters, undefined, { enabled: !useInfinite });

  const serverSortBy: PropertySortOption =
    sortBy === 'nearest' ? 'newest' : (sortBy as PropertySortOption);

  const {
    data: infiniteData,
    isLoading: isInfiniteLoading,
    isError: isInfiniteError,
    isFetching: isInfiniteFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchInfinite,
  } = useInfiniteProperties(useInfinite ? combinedFilters : undefined, serverSortBy);

  const properties = useInfinite
    ? (infiniteData?.pages.flatMap((p) => p.rows) ?? [])
    : (nearbyProperties ?? []);
  const isLoading = useInfinite ? isInfiniteLoading : isNearbyLoading;
  const isError = useInfinite ? isInfiniteError : isNearbyError;
  // Background refresh (not the initial load, not the "load more" page)
  const isRefreshing = useInfinite
    ? isInfiniteFetching && !isInfiniteLoading && !isFetchingNextPage
    : isNearbyFetching && !isNearbyLoading;
  const retrySearch = useCallback(() => {
    if (useInfinite) refetchInfinite();
    else refetchNearby();
  }, [useInfinite, refetchInfinite, refetchNearby]);
  const clearAllFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: false });
  }, [setSearchParams]);

  const { data: totalCount } = usePropertiesCount(combinedFilters);

  // Fetch ranked listings when filtering by a specific neighborhood
  const { data: rankedListings, isLoading: isLoadingRanked } = useRankedListingsByNeighborhood(
    filters.neighborhood,
    6
  );

  const hasRankedListings = rankedListings && rankedListings.length > 0;

  // Add distance to properties when in nearby mode
  const propertiesWithDistance = useMemo(() => {
    if (!properties.length) return [];
    if (!isNearby || !nearbyLat || !nearbyLng) {
      return properties.map((p) => ({ ...p, _distance: undefined as number | undefined }));
    }
    const userLat = parseFloat(nearbyLat);
    const userLng = parseFloat(nearbyLng);
    return properties.map((p) => ({
      ...p,
      _distance:
        p.latitude && p.longitude ? calculateDistance(userLat, userLng, p.latitude, p.longitude) : undefined,
    }));
  }, [properties, isNearby, nearbyLat, nearbyLng]);

  // In infinite mode the server already returns rows in the right order, so
  // we only re-sort client-side for nearby mode (distance) or when no rows yet.
  const sortedProperties = useMemo(() => {
    if (!propertiesWithDistance.length) return [];
    if (useInfinite) return propertiesWithDistance;
    const sorted = [...propertiesWithDistance];
    switch (sortBy) {
      case 'nearest':
        return sorted.sort((a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity));
      case 'price-asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sorted.sort((a, b) => b.price - a.price);
      case 'beds-desc':
        return sorted.sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0));
      case 'newest':
      default:
        return sorted.sort(
          (a, b) => new Date(b.listing_date || 0).getTime() - new Date(a.listing_date || 0).getTime()
        );
    }
  }, [propertiesWithDistance, sortBy, useInfinite]);

  // IntersectionObserver sentinel for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!useInfinite || !hasNextPage || isFetchingNextPage) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [useInfinite, hasNextPage, isFetchingNextPage, fetchNextPage]);


  // Generate dynamic SEO meta based on filters
  const generatePageTitle = () => {
    if (filters.neighborhood) {
      return `Homes for Sale in ${filters.neighborhood}, ${effectiveCity} | Book Viewing`;
    }
    if (filters.neighborhoods?.length) {
      const regionTitle = getEdmontonRegionTitle(region);
      return `${regionTitle || 'Homes'} for Sale | Book Viewing`;
    }
    if (filters.propertyType) {
      return `${filters.propertyType}s for Sale in ${effectiveCity} | Book Viewing`;
    }
    return `Homes for Sale in ${effectiveCity} | Search Properties & Book Viewings`;
  };

  const generateMetaDescription = () => {
    const count = totalCount ? `${totalCount.toLocaleString()}+ ` : '';
    if (filters.neighborhood) {
      return `Browse ${count}homes for sale in ${filters.neighborhood}, ${effectiveCity}. View photos, prices, and book viewings online—no phone calls needed.`;
    }
    if (filters.propertyType) {
      return `Find ${count}${filters.propertyType.toLowerCase()}s for sale in ${effectiveCity}. Search listings, compare prices, and schedule viewings online.`;
    }
    return `Search ${count}homes for sale in ${effectiveCity}. Browse houses, condos, townhouses and book viewings online—no phone calls required.`;
  };

  const canonicalUrl = `https://bookviewing.ca/search${filters.neighborhood ? `?neighborhood=${encodeURIComponent(filters.neighborhood)}` : ''}`;

  // noindex filtered search pages to prevent duplicate/canonical issues in Search Console
  const hasFilterParams = !!(
    filters.minPrice || filters.maxPrice || filters.minBedrooms || filters.minBathrooms ||
    filters.minSqft || filters.maxSqft || filters.minYearBuilt || filters.maxYearBuilt ||
    filters.parking || filters.propertyType || filters.searchQuery || filters.distressed ||
    isNearby || region
  );
  const shouldNoIndex = hasFilterParams && !filters.neighborhood;

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{generatePageTitle()}</title>
        <meta name="description" content={generateMetaDescription()} />
        <link rel="canonical" href={canonicalUrl} />
        {shouldNoIndex && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={generatePageTitle()} />
        <meta property="og:description" content={generateMetaDescription()} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Book Viewing" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={generatePageTitle()} />
        <meta name="twitter:description" content={generateMetaDescription()} />
      </Helmet>

      <Header />

      <main className="flex-1 bg-secondary/30">
        {/* Back Button */}
        <div className="border-b bg-card">
          <div className="container py-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
          </div>
        </div>

        {/* Search Header */}
        <div className="bg-card border-b py-4">
          <div className="container">
            <SearchBar
              initialFilters={filters}
              onSearch={handleSearch}
              filtersOpen={filtersOpen}
              onFiltersOpenChange={setFiltersOpen}
              extraAction={<SaveSearchDialog filters={filters} />}
            />
          </div>
        </div>

        {/* Results */}
        <div className="container py-8">
          {/* Neighborhood Intro - Only shown when filtering by neighborhood */}
          {filters.neighborhood && (
            <NeighborhoodIntro neighborhood={filters.neighborhood} city={effectiveCity} />
          )}

          {/* Top Ranked Listings Section - Only shown when filtering by neighborhood */}
          {filters.neighborhood && (isLoadingRanked || hasRankedListings) && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-white">
                  <Trophy className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Top Ranked in {filters.neighborhood}</h3>
                  <p className="text-sm text-muted-foreground">
                    Highest-scored listings based on value, photos, and freshness
                  </p>
                </div>
              </div>
              
              {isLoadingRanked ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="space-y-3">
                      <Skeleton className="h-48 w-full rounded-lg" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rankedListings?.map((listing) => (
                    <RankedPropertyCard 
                      key={listing.id} 
                      rankedListing={listing} 
                      showBreakdown={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-2xl font-semibold">
                  {isNearby
                    ? 'Properties Near You'
                    : isDistressedMode
                      ? 'Distressed Properties'
                      : filters.neighborhood
                        ? `Homes for Sale in ${filters.neighborhood}`
                        : filters.neighborhoods?.length
                          ? `Homes for Sale in ${getEdmontonRegionTitle(region) ?? 'Selected Area'}`
                          : filters.searchQuery
                            ? `Results for "${filters.searchQuery}"`
                            : 'All Properties'}
                </h2>
                {isNearby && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    Nearby
                  </span>
                )}
                {isDistressedMode && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-destructive/10 text-destructive rounded-full">
                    {filters.distressedType === 'bank'
                      ? 'Bank Owned'
                      : filters.distressedType === 'court'
                        ? "Court of King's Bench"
                        : filters.distressedType === 'probate'
                          ? 'Probate / Estate'
                          : 'Foreclosures'}
                  </span>
                )}
                {!filters.neighborhood && !isNearby && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                    {effectiveCity}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground">
                {isLoading 
                  ? 'Loading...' 
                  : isNearby
                    ? `${totalCount?.toLocaleString() ?? sortedProperties.length} properties sorted by distance`
                    : filters.searchQuery && !filters.city
                      ? `${totalCount?.toLocaleString() ?? sortedProperties.length} properties found`
                      : `${totalCount?.toLocaleString() ?? sortedProperties.length} properties found in ${effectiveCity}`}
              </p>
              {isDistressedMode && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {([
                    { key: undefined, label: 'All Distressed' },
                    { key: 'bank' as const, label: 'Bank Owned' },
                    { key: 'court' as const, label: "Court of King's Bench" },
                    { key: 'probate' as const, label: 'Probate / Estate' },
                  ]).map((opt) => {
                    const active = (filters.distressedType ?? undefined) === opt.key;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => handleSearch({ ...filters, distressedType: opt.key })}
                        className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                          active
                            ? 'bg-foreground text-background border-foreground'
                            : 'bg-background text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </Button>

              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {isNearby && <SelectItem value="nearest">Nearest</SelectItem>}
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="beds-desc">Most Bedrooms</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Property Grid/List */}
          {viewMode === 'grid' ? (
            <PropertyGrid
              properties={sortedProperties}
              isLoading={isLoading}
              isError={isError}
              onRetry={retrySearch}
              isFetching={isRefreshing}
              emptyMessage="Try widening your price range, removing a filter, or searching a nearby area."
              emptyAction={
                hasFilterParams ? (
                  <Button variant="outline" onClick={clearAllFilters}>
                    Clear all filters
                  </Button>
                ) : undefined
              }
            />
          ) : isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-40 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {sortedProperties.map((property) => (
                <PropertyCard key={property.id} property={property} variant="compact" distanceKm={property._distance} />
              ))}
            </div>
          )}

          {/* Infinite-scroll sentinel + status */}
          {useInfinite && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Loading more properties…</span>
                </div>
              ) : !hasNextPage && sortedProperties.length > 0 ? (
                <span className="text-sm text-muted-foreground">
                  You've reached the end of the results.
                </span>
              ) : null}
            </div>
          )}

          {/* Viewing Plan CTA Banner */}
          <div className="mt-12">
            <ViewingPlanCTA variant="banner" />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}